import { useRef } from 'react';

/**
 * 제출 중복 방지를 위한 throttle 간격(ms).
 * @description
 * - loading·disabled는 렌더 파생값이라, 첫 제출이 상태를 켜도 그 리렌더가 커밋되기 전까지는
 *   여전히 제출을 통과시킨다. 이 간극만 덮으면 되므로 렌더 한 번에 필요한 만큼만 잡는다.
 * - 네트워크 왕복처럼 긴 비동기 작업은 이 값으로 덮지 않는다. 소비처가 loading에 반영해야 한다.
 */
const SUBMIT_THROTTLE_MS = 100;

/**
 * 제출 핸들러를 leading edge throttle로 감싼 함수를 반환합니다.
 * @description
 * - 직전 호출로부터 `SUBMIT_THROTTLE_MS` 이내의 호출은 핸들러를 실행하지 않고 무시합니다.
 * - 직전 호출 시각을 ref에 두므로 리렌더 커밋 여부와 무관하게 동기적으로 판정합니다.
 * - 반환 함수는 매 렌더 새로 만들어지지만 판정 상태가 ref에 있어 throttle 구간이 유지되고,
 *   항상 최신 `handler`를 호출합니다. 메모이제이션이 필요하지 않습니다.
 * @example
 * const throttledSubmit = useThrottledSubmit(onSubmit);
 *
 * const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
 *   e.preventDefault();
 *   throttledSubmit(e);
 * };
 */
const useThrottledSubmit = <TArg>(handler?: (arg: TArg) => void) => {
  const lastCalledAtRef = useRef(0);

  return (arg: TArg) => {
    const now = Date.now();

    if (now - lastCalledAtRef.current < SUBMIT_THROTTLE_MS) {
      return;
    }

    lastCalledAtRef.current = now;
    handler?.(arg);
  };
};

export { SUBMIT_THROTTLE_MS, useThrottledSubmit };
